import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import {
  addEntity,
  removeEntity,
  setAllEntities,
  setEntity,
  withEntities,
} from '@ngrx/signals/entities';
import { Dayjs } from 'dayjs';
import { Activity, ActivityTypeEnum } from '../components/organisms/activities/activities.types';
import { ToastTypeEnum } from '../components/organisms/toast/toast.type';
import { RequestStateEnum } from '../enums/app.enum';
import { ActivityService } from '../services/activity.service';
import { ToastService } from '../services/toast.service';

interface ActivityState {
  requestState: RequestStateEnum | null;
}

const initialState: ActivityState = { requestState: null };

export const ActivityStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withEntities<Activity>(),
  withMethods((store) => {
    const activityService = inject(ActivityService);
    const toastService = inject(ToastService);

    return {
      async loadInRange(start: Dayjs, end: Dayjs, pageSize: number, pageIndex: number) {
        patchState(store, { requestState: RequestStateEnum.PENDING });

        activityService.fetchActivitiesInRange(start, end, pageSize, pageIndex).subscribe({
          next: (response) => {
            patchState(store, { requestState: RequestStateEnum.SUCCESS });
            patchState(store, setAllEntities(response.payload));
          },
          error: () => {
            patchState(store, { requestState: RequestStateEnum.ERROR });
            toastService.addToast('Aktivitäten konnten nicht geladen werden', ToastTypeEnum.ERROR);
          },
        });
      },

      async create(
        type: ActivityTypeEnum,
        title: string,
        description: string,
        date: Dayjs,
        time: string | null
      ) {
        patchState(store, { requestState: RequestStateEnum.PENDING });

        activityService.createActivity(type, title, description, date, time).subscribe({
          next: (response) => {
            patchState(store, { requestState: RequestStateEnum.SUCCESS });
            patchState(store, addEntity(response));
          },
          error: () => {
            patchState(store, { requestState: RequestStateEnum.ERROR });
            toastService.addToast('Aktivitäten konnten nicht erstellt werden', ToastTypeEnum.ERROR);
          },
        });
      },

      async update(
        id: string,
        type: ActivityTypeEnum,
        title: string,
        description: string,
        date: Dayjs,
        time: string | null
      ) {
        patchState(store, { requestState: RequestStateEnum.PENDING });

        activityService.updateActivity(id, type, title, description, date, time).subscribe({
          next: (response) => {
            patchState(store, { requestState: RequestStateEnum.SUCCESS });
            patchState(store, setEntity(response));
          },
          error: () => {
            patchState(store, { requestState: RequestStateEnum.ERROR });
            toastService.addToast('Änderungen wurden nicht gespeichert', ToastTypeEnum.ERROR);
          },
        });
      },

      async delete(id: string) {
        patchState(store, { requestState: RequestStateEnum.PENDING });

        activityService.delteActivity(id).subscribe({
          next: () => {
            patchState(store, { requestState: RequestStateEnum.SUCCESS });
            patchState(store, removeEntity(id));
          },
          error: () => {
            patchState(store, { requestState: RequestStateEnum.ERROR });
            toastService.addToast('Aktivität wurden nicht gelöscht', ToastTypeEnum.ERROR);
          },
        });
      },
    };
  })
);
